import { useState, useRef, useCallback } from "react"
import { useTranslation } from "react-i18next"
import { Upload, X, Loader2 } from "lucide-react"
import { toast } from "sonner"
import { api } from "@/lib/api"
import { downscaleImageIfNeeded } from "@/lib/image-downscale"
import { useAuthMe } from "@/hooks/useAuthMe"
import { ImageWithFallback } from "@/components/common/ImageWithFallback"
import type { FrameImage } from "@/types/generation"

interface FrameImageUploadProps {
  firstFrame: FrameImage | null
  lastFrame: FrameImage | null
  onFirstFrameChange: (frame: FrameImage | null) => void
  onLastFrameChange: (frame: FrameImage | null) => void
  disabled?: boolean
}

/**
 * 프레임 이미지 업로드 컴포넌트
 * 첫 프레임과 마지막 프레임 이미지를 업로드할 수 있습니다.
 */
export function FrameImageUpload({
  firstFrame,
  lastFrame,
  onFirstFrameChange,
  onLastFrameChange,
  disabled = false,
}: FrameImageUploadProps) {
  const { t } = useTranslation()
  // DRM 테넌트의 파일은 브라우저가 열 수 없는 암호화 바이너리라 클라이언트 치수 검증을 건너뛰고 서버(복호화 후) 검증에 맡긴다
  const isDrmActive = useAuthMe().data?.isDrmActive === true
  const [isUploadingFirst, setIsUploadingFirst] = useState(false)
  const [isUploadingLast, setIsUploadingLast] = useState(false)
  const [isDraggingFirst, setIsDraggingFirst] = useState(false)
  const [isDraggingLast, setIsDraggingLast] = useState(false)
  const firstInputRef = useRef<HTMLInputElement>(null)
  const lastInputRef = useRef<HTMLInputElement>(null)

  const validateImageDimensions = useCallback(
    (file: File): Promise<{ width: number; height: number }> =>
      new Promise((resolve, reject) => {
        const img = new Image()
        img.onload = () => {
          URL.revokeObjectURL(img.src)
          resolve({ width: img.naturalWidth, height: img.naturalHeight })
        }
        img.onerror = () => {
          URL.revokeObjectURL(img.src)
          reject(new Error(t('generator.imageReadFailed')))
        }
        img.src = URL.createObjectURL(file)
      }),
    [t]
  )

  const MIN_IMAGE_DIMENSION = 300
  /** 업로드 전 축소 기준: 긴 변 2048px, 10MB (서버 상한과 동일) */
  const MAX_IMAGE_EDGE = 2048
  const MAX_UPLOAD_BYTES = 10 * 1024 * 1024

  const uploadFrame = useCallback(
    async (file: File, role: "first_frame" | "last_frame") => {
      const setLoading = role === "first_frame" ? setIsUploadingFirst : setIsUploadingLast
      const onFrameChange = role === "first_frame" ? onFirstFrameChange : onLastFrameChange

      setLoading(true)
      try {
        // 큰 사진은 업로드 상한(10MB)에 걸리기 전에 브라우저에서 줄인다. 짧은 변이 최소 크기보다
        // 작아지는 축소는 하지 않는다. DRM 테넌트의 암호화 파일은 디코딩할 수 없어 원본을 보낸다.
        const prepared = isDrmActive
          ? file
          : await downscaleImageIfNeeded(file, {
              maxEdge: MAX_IMAGE_EDGE,
              maxBytes: MAX_UPLOAD_BYTES,
              minShortEdge: MIN_IMAGE_DIMENSION,
            })

        const { width, height } = isDrmActive
          ? { width: Number.POSITIVE_INFINITY, height: Number.POSITIVE_INFINITY }
          : await validateImageDimensions(prepared)
        if (width < MIN_IMAGE_DIMENSION || height < MIN_IMAGE_DIMENSION) {
          toast.error(t('generator.imageTooSmallTitle'), {
            description: t('generator.imageTooSmallDescription', {
              min: MIN_IMAGE_DIMENSION,
              width,
              height,
            }),
          })
          return
        }

        const formData = new FormData()
        formData.append("files", prepared, prepared.name)
        formData.append("roles", JSON.stringify([role]))

        // api.post 로 보내야 응답의 `/api/...` 프록시 URL 이 배포 경로(`/video/api/...`)로 변환된다.
        // 직접 fetch 하면 미리보기 <img> 가 기본 경로 없는 주소를 불러 깨진 이미지가 된다.
        const uploaded = await api.post<FrameImage | FrameImage[]>("/generate/frame-images", formData)
        const frame: FrameImage = Array.isArray(uploaded) ? uploaded[0]! : uploaded
        onFrameChange(frame)
        toast.success(t('generator.uploadSuccessTitle'))
      } catch (error) {
        const message =
          error instanceof Error ? error.message : t('generator.uploadFailedFallback')
        toast.error(t('generator.uploadFailedTitle'), { description: message })
      } finally {
        setLoading(false)
      }
    },
    [onFirstFrameChange, onLastFrameChange, validateImageDimensions, isDrmActive, t]
  )

  const handleFileSelect = useCallback(
    (files: FileList | null, role: "first_frame" | "last_frame") => {
      if (!files || files.length === 0 || disabled) return
      uploadFrame(files[0], role)
    },
    [disabled, uploadFrame]
  )

  const handleRemoveFirst = useCallback(() => {
    onFirstFrameChange(null)
    // last_frame도 함께 제거 (store에서 처리하지만 UI 즉시 반영)
    if (firstInputRef.current) firstInputRef.current.value = ""
  }, [onFirstFrameChange])

  const handleRemoveLast = useCallback(() => {
    onLastFrameChange(null)
    if (lastInputRef.current) lastInputRef.current.value = ""
  }, [onLastFrameChange])

  const handleDrop = useCallback(
    (e: React.DragEvent, role: "first_frame" | "last_frame") => {
      e.preventDefault()
      e.stopPropagation()
      if (role === "first_frame") setIsDraggingFirst(false)
      else setIsDraggingLast(false)

      const file = e.dataTransfer.files?.[0]
      if (!file || !file.type.startsWith("image/")) return
      handleFileSelect(e.dataTransfer.files, role)
    },
    [handleFileSelect]
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
  }, [])

  const handleDragEnter = useCallback(
    (e: React.DragEvent, role: "first_frame" | "last_frame") => {
      e.preventDefault()
      e.stopPropagation()
      if (role === "first_frame") setIsDraggingFirst(true)
      else setIsDraggingLast(true)
    },
    []
  )

  const handleDragLeave = useCallback(
    (e: React.DragEvent, role: "first_frame" | "last_frame") => {
      e.preventDefault()
      e.stopPropagation()
      if (role === "first_frame") setIsDraggingFirst(false)
      else setIsDraggingLast(false)
    },
    []
  )

  const isLastFrameDisabled = disabled || !firstFrame

  return (
    <div className="flex gap-3">
      {/* 첫 프레임 슬롯 */}
      <div className="flex-1 flex flex-col items-center gap-1.5">
        <div
          className={`relative w-full aspect-square max-w-[150px] rounded-lg overflow-hidden ${
            firstFrame
              ? "bg-slate-100"
              : `border-2 border-dashed transition-colors ${
                  isDraggingFirst
                    ? "border-primary bg-primary/10"
                    : disabled
                      ? "border-slate-200 bg-slate-50"
                      : "border-slate-300 hover:border-primary/50 bg-slate-50 hover:bg-slate-100 cursor-pointer"
                }`
          }`}
          onClick={
            !firstFrame && !disabled && !isUploadingFirst
              ? () => firstInputRef.current?.click()
              : undefined
          }
          onDrop={!firstFrame && !disabled && !isUploadingFirst ? (e) => handleDrop(e, "first_frame") : undefined}
          onDragOver={!firstFrame && !disabled ? handleDragOver : undefined}
          onDragEnter={!firstFrame && !disabled ? (e) => handleDragEnter(e, "first_frame") : undefined}
          onDragLeave={!firstFrame && !disabled ? (e) => handleDragLeave(e, "first_frame") : undefined}
        >
          {firstFrame ? (
            <>
              <ImageWithFallback
                src={firstFrame.thumbnailUrl || firstFrame.url}
                alt={t('generator.firstFrame')}
                className="w-full h-full object-cover"
              />
              {!disabled && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemoveFirst()
                  }}
                  className="absolute top-1 right-1 w-5 h-5 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center transition-opacity"
                >
                  <X className="w-3 h-3 text-white" />
                </button>
              )}
            </>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-1">
              {isUploadingFirst ? (
                <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
              ) : (
                <Upload className="w-5 h-5 text-slate-400" />
              )}
            </div>
          )}
        </div>
        <span className="text-xs text-slate-500">{t('generator.firstFrame')}</span>
      </div>

      {/* 마지막 프레임 슬롯 */}
      <div className="flex-1 flex flex-col items-center gap-1.5">
        <div
          className={`relative w-full aspect-square max-w-[150px] rounded-lg overflow-hidden ${
            lastFrame
              ? "bg-slate-100"
              : `border-2 border-dashed transition-colors ${
                  isDraggingLast
                    ? "border-primary bg-primary/10"
                    : isLastFrameDisabled
                      ? "border-slate-200 bg-slate-50 opacity-50"
                      : "border-slate-300 hover:border-primary/50 bg-slate-50 hover:bg-slate-100 cursor-pointer"
                }`
          }`}
          onClick={
            !lastFrame && !isLastFrameDisabled && !isUploadingLast
              ? () => lastInputRef.current?.click()
              : undefined
          }
          onDrop={!lastFrame && !isLastFrameDisabled && !isUploadingLast ? (e) => handleDrop(e, "last_frame") : undefined}
          onDragOver={!lastFrame && !isLastFrameDisabled ? handleDragOver : undefined}
          onDragEnter={!lastFrame && !isLastFrameDisabled ? (e) => handleDragEnter(e, "last_frame") : undefined}
          onDragLeave={!lastFrame && !isLastFrameDisabled ? (e) => handleDragLeave(e, "last_frame") : undefined}
        >
          {lastFrame ? (
            <>
              <ImageWithFallback
                src={lastFrame.thumbnailUrl || lastFrame.url}
                alt={t('generator.lastFrame')}
                className="w-full h-full object-cover"
              />
              {!disabled && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemoveLast()
                  }}
                  className="absolute top-1 right-1 w-5 h-5 bg-black/60 hover:bg-black/80 rounded-full flex items-center justify-center transition-opacity"
                >
                  <X className="w-3 h-3 text-white" />
                </button>
              )}
            </>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center gap-1">
              {isUploadingLast ? (
                <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
              ) : (
                <Upload className="w-5 h-5 text-slate-400" />
              )}
            </div>
          )}
        </div>
        <span className="text-xs text-slate-500">{t('generator.lastFrame')}</span>
      </div>

      {/* 숨겨진 파일 입력 */}
      <input
        ref={firstInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          handleFileSelect(e.target.files, "first_frame")
          if (firstInputRef.current) firstInputRef.current.value = ""
        }}
        disabled={disabled || isUploadingFirst}
      />
      <input
        ref={lastInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          handleFileSelect(e.target.files, "last_frame")
          if (lastInputRef.current) lastInputRef.current.value = ""
        }}
        disabled={isLastFrameDisabled || isUploadingLast}
      />
    </div>
  )
}
